<!--Loading Spinner-->
@if (loading$ | async) {
  <ds-localized-spinner
    class="block pad-bottom-20"
    [dataLoading]="loading$ | async"
    [loadingMessage]="'Looking for automatic payment series...'"
  ></ds-localized-spinner>
}

@if ((loading$ | async) === false && !newBillingDesign) {
  <div>
    <!--Title-->
    @if (!tableOnly) {
      <p dsContent [contentPath]="'billing.manageAutoPayText'"></p>
    }
    <!--Automatic Payments Table-->
    @if ((tableRows$ | async)?.length > 0) {
      <ds-auto-pay-detail-table
        [tableRows]="tableRows$ | async"
        [tableColumns]="tableColumns"
        [hideActions]="tableOnly"
      ></ds-auto-pay-detail-table>
    }
    <!--No AutoPay Setup Content-->
    @if ((tableRows$ | async)?.length === 0) {
      <div>
        <p class="pad-bottom-20" dsContent [contentPath]="'billing.noAutopayRulesP1'"></p>
        @if (hasAdvancePolicy$ | async) {
          <p class="pad-bottom-20" dsContent [contentPath]="noAutoPaySetupContentPath$ | async"></p>
        }
      </div>
    }
    <!--Setup AutoPay Button-->
    @if (!tableOnly && (autoPayEligible$ | async)) {
      <button
        ds-button
        class="-offset"
        aria-label="Set Up AutoPay"
        [disabled]="(autoPayEligible$ | async) === false"
        (click)="setupAutoPay()"
        authCheck
        [permissionName]="'makeautopayrule_walkthrough'"
        [checkAction]="'disable'"
        data-cy="setUpAutoPayBtn"
        dsContent
        [contentPath]="'billing.setUpAutoPay'"
      ></button>
    }
  </div>
}

<!--Modals-->
<ds-silent-registration-error-modal></ds-silent-registration-error-modal>
<ds-auto-pay-delete-modal-wrapper
  [displayDeleteAutoPayDiscountMessage]="displayDeleteAutoPayDiscountMessage$ | async"
  (deleteConfirmEvent)="deleteAutoPayConfirm()"
></ds-auto-pay-delete-modal-wrapper>

<!--Table Templates-->
<ng-template #headerCellTpl let-column="column">
  <div class="header-cell table-header-cell-wrapper">
    <span
      [ngClass]="{
        'none lg-block': column.name && column?.shortName,
        block: column?.name && !column?.shortName
      }"
      >{{ column?.name }}</span
    >
    @if (!!column?.shortName) {
      <span class="block lg-none">{{ column?.shortName }}</span>
    }
  </div>
</ng-template>

<ng-template #billAccountNameCellTpl let-row="row" let-expanded="expanded">
  <div class="ds-table-body-cell-header-wrapper">
    <div class="block md-none ds-table-body-cell-header">
      <h3 class="margin-0" title="{{ row.billAccountNumber }}">
        <a
          class="cell"
          [title]="row.billAccountNumber"
          (click)="billAccountClick(row.billAccountNumber)"
          >{{ row.billAccountNumber | prettyBillingAcctNum }}</a
        >
      </h3>
    </div>

    <div class="none md-flex table-body-cell-label-content expandable-cell">
      <span class="overflow-hidden overflow-ellipsis">
        <a
          class="cell"
          [title]="row.billAccountNumber"
          (click)="billAccountClick(row.billAccountNumber)"
          >{{ row.billAccountNumber | prettyBillingAcctNum }}</a
        >
      </span>
    </div>
  </div>
</ng-template>

<ng-template #amountCellTpl let-row="row">
  <div class="table-body-cell-label-content expandable-cell">
    {{ row.paymentAmount }}
  </div>
</ng-template>

<ng-template #paymentDateCellTpl let-row="row">
  <div class="table-body-cell-label-content expandable-cell">
    {{ row.paymentDate }}
  </div>
</ng-template>

<ng-template #paymentAccountCellTpl let-row="row" let-expanded="expanded">
  @if (!row.nickName) {
    <ds-payment-method-block
      class="cell-component"
      [paymentAccount]="row.paymentMethod"
      [rowExpanded]="expanded"
    >
    </ds-payment-method-block>
  }
  @if (!!row.nickName) {
    <ds-payment-method-block
      class="cell-component"
      [rowExpanded]="expanded"
      [paymentAccount]="row.paymentMethod"
      [nickName]="row.nickName"
    >
    </ds-payment-method-block>
  }
</ng-template>

<ng-template #editCellTpl let-row="row" let-expanded="expanded" let-rowIndex="rowIndex">
  <div class="table-body-cell-label-content expandable-cell">
    <div class="flex md-none datatable-body-cell-label-actions mobile-actions">
      <!-- Mobile view - swap order -->
      <button
        ds-button
        authCheck
        [permissionName]="'editautopayrule_walkthrough'"
        [checkAction]="'disable'"
        class="-outline-white margin-right-12"
        title="Edit this automatic payment series"
        (click)="editAutoPayClick(row.billAccountNumber)"
        aria-label="Edit"
        data-cy="billAccountPaymentsAutomaticMobileEditButton"
      >
        Edit
      </button>
      <a
        authCheck
        [permissionName]="'deleteautopayrule'"
        [checkAction]="'hide'"
        href="javascript:void(0);"
        class="error-600-text caption"
        title="Delete this automatic payment series"
        (click)="deleteAutoPayClick(row.billAccountNumber)"
        data-cy="billAccountPaymentsAutomaticMobileDeleteButton"
      >
        Delete
      </a>
    </div>

    @if (!expanded) {
      <div class="none md-flex datatable-body-cell-label-actions desktop-actions">
        <!-- Desktop view -->
        <a
          authCheck
          [permissionName]="'deleteautopayrule'"
          [checkAction]="'hide'"
          href="javascript:void(0);"
          class="error-600-text caption"
          title="Delete this automatic payment series"
          (click)="deleteAutoPayClick(row.billAccountNumber)"
          data-cy="billAccountPaymentsAutomaticDeleteLink"
        >
          Delete
        </a>
        <button
          ds-button
          authCheck
          [permissionName]="'editautopayrule_walkthrough'"
          [checkAction]="'disable'"
          class="-outline-white"
          title="Edit this automatic payment series"
          (click)="editAutoPayClick(row.billAccountNumber)"
          aria-label="Edit"
          data-cy="billAccountPaymentsAutomaticEditButton"
        >
          Edit
        </button>
      </div>
    }
  </div>
</ng-template>

@if (newBillingDesign) {
  @if (loadingBillingDetails$ | async) {
    <ds-localized-spinner
      class="block pad-bottom-20"
      [dataLoading]="loadingBillingDetails$ | async"
      [loadingMessage]="'Looking for billing account details...'"
    ></ds-localized-spinner>
  }
}

@if (
  (loading$ | async) === false && (loadingBillingDetails$ | async) === false && newBillingDesign
) {
  <div class="align-center grey-background">
    <div
      class="manage-autopay-container"
      [ngClass]="{ 'display-enrolled-tiles-only': displayEnrolledTiles }"
    >
      @if (!displayEnrolledTiles) {
        <div
          class="header font-weight-semibold"
          dsContent
          [contentPath]="'billing.manageAutopay.autopayHeader'"
        ></div>
        <div
          class="header-info font-size-1"
          dsContent
          [contentPath]="'billing.manageAutopay.autopayHeaderInfo'"
        ></div>
      }
      @if (autopayAccountsList.notEnrolledAccounts.length && !displayEnrolledTiles) {
        <div
          class="sub-header font-weight-semibold"
          dsContent
          [contentPath]="'billing.manageAutopay.autopaySubHeaderNotEnrolled'"
        ></div>
        @for (billAccount of autopayAccountsList.notEnrolledAccounts; track billAccount.riskDesc) {
          <div class="tile">
            <div>
              <div
                class="error-pill pill font-weight-bold"
                dsContent
                [contentPath]="'billing.manageAutopay.autopayErrorPill'"
              ></div>
              @for (risk of billAccount.riskDesc; track risk) {
                <div class="risk-desc">{{ risk }}</div>
              }
            </div>
            <div class="action-container">
              <div
                class="action-btn font-weight-semibold -offset"
                [disabled]="(autoPayEligible$ | async) === false"
                (click)="setUpAutopay(billAccount?.accountNumber)"
                authCheck
                [permissionName]="'makeautopayrule_walkthrough'"
                [checkAction]="'disable'"
                data-cy="setUpAutoPayBtn"
              >
                <span dsContent [contentPath]="'billing.manageAutopay.setUp'"></span>
                <img src="/assets/images/chevron-right-thick.svg" />
              </div>
            </div>
          </div>
        }
      }
      @if (autopayAccountsList.enrolledAccounts.length) {
        @if (!displayEnrolledTiles) {
          <div
            class="sub-header font-weight-semibold second-section"
            dsContent
            [contentPath]="'billing.manageAutopay.autopaySubHeaderEnrolled'"
          ></div>
        }
        @for (billAccount of autopayAccountsList.enrolledAccounts; track billAccount.riskDesc) {
          <div
            class="tile"
            [ngClass]="{ 'hide-border-bottom margin-bottom-0': billAccount.enableEdit }"
          >
            <div>
              <div
                class="success-pill pill font-weight-bold"
                dsContent
                [contentPath]="'billing.manageAutopay.autopaySuccessPill'"
              ></div>
              @for (risk of billAccount.riskDesc; track risk) {
                <div class="risk-desc">{{ risk }}</div>
              }
            </div>
            @if (!displayEnrolledTiles) {
              <div class="action-container">
                @if (!billAccount.enableEdit) {
                  <div
                    class="action-btn font-weight-semibold"
                    (click)="editAutoPayClickToggle(billAccount.accountNumber)"
                  >
                    <span dsContent [contentPath]="'billing.editAutopay'"></span>
                    <img src="/assets/images/chevron-right-thick.svg" />
                  </div>
                } @else {
                  <div
                    class="action-close-btn"
                    (click)="editAutoPayClickToggle(billAccount.accountNumber)"
                  >
                    <img src="/assets/images/close-icon.svg" />
                  </div>
                }
              </div>
            }
            <!-- edit section -->
          </div>
          @if (billAccount.enableEdit) {
            <div class="row expanded-edit">
              <div class="row column twelve font-size-1 flex info-container">
                <img class="margin-right-8" src="/assets/images/complete-circle.svg" />
                <span
                  class="height-font-1 neutral-800"
                  dsContent
                  [contentPath]="'billing.manageAutopay.autopayScheduled'"
                >
                </span>
                <span>&nbsp;</span>
                <span>
                  {{ autoPayAccounts[0]?.nextPaymentDate | dateFormat: 'MMM D' }}
                  @if (autoPayAccounts[0]?.predictedDollarAmount !== 'UNKNOWN') {
                    for {{ autoPayAccounts[0]?.predictedDollarAmount | currencyUSD }}
                  }
                </span>
              </div>
              <div class="column tweleve md-six">
                <div
                  class="font-weight-bold font-size-1 height-font-1 neutral-800"
                  dsContent
                  [contentPath]="'billing.manageAutopay.paymentMethodHeader'"
                ></div>
                <div>
                  <span class="height-font-1 neutral-600">
                    {{ billAccount.automaticPayment[0]?.paymentAccount?.nickName }}
                  </span>
                  @if (billAccount?.paymentMethod.length > 0) {
                    <span
                      >(ending in
                      @if (billAccount?.paymentMethod[0]?.creditCard) {
                        <span>
                          {{
                            billAccount?.paymentMethod[0]?.creditCard?.cardNumber | returnLast: 4
                          }}
                        </span>
                      }

                      @if (billAccount?.paymentMethod[0]?.achWithdrawal) {
                        <span>{{
                          billAccount?.paymentMethod[0]?.achWithdrawal?.accountNumber
                            | returnLast: 3
                        }}</span>
                      }
                      )</span
                    >
                  }
                  <span
                    class="action-btn font-weight-semibold"
                    dsContent
                    [contentPath]="'billing.editAutopay'"
                    (click)="editpaymentMethod(billAccount?.accountNumber, 'paymentMethod')"
                  >
                  </span>
                </div>
              </div>
              <div class="column twelve md-six">
                <div
                  class="font-weight-bold font-size-1 height-font-1 neutral-800"
                  dsContent
                  [contentPath]="'billing.manageAutopay.paymentDateHeader'"
                ></div>
                <div>
                  <span class="height-font-1 neutral-600">
                    @if (billAccount.automaticPayment[0]?.paymentAmount === 'MinDue') {
                      @if (billAccount.automaticPayment[0]?.daysBeforeDueDate === '0') {
                        <span dsContent [contentPath]="'billing.onTheDueDate'"></span>
                      } @else if (billAccount.automaticPayment[0]?.daysBeforeDueDate === '1') {
                        <span dsContent [contentPath]="'billing.oneDayBefore'"></span>
                      } @else {
                        <span
                          dsContent
                          [contentPath]="'billing.daysBefore'"
                          [options]="{ days: billAccount.automaticPayment[0]?.daysBeforeDueDate }"
                        ></span>
                      }
                    } @else {
                      <span dsContent [contentPath]="'billing.manageAutopay.allPaidAtOnce'"></span>
                    }
                  </span>
                  <span
                    class="action-btn font-weight-semibold"
                    dsContent
                    [contentPath]="'billing.editAutopay'"
                    (click)="editpaymentMethod(billAccount?.accountNumber, 'paymentDate')"
                  >
                  </span>
                </div>
              </div>
              <div
                class="delete-button font-weight-semibold"
                (click)="deleteAlertPopup(billAccount.accountNumber)"
                dsContent
                [contentPath]="'billing.manageAutopay.deleteAutopayBtn'"
              ></div>
            </div>
          }
        }
      }
    </div>
  </div>
}

<!-- Modal for Edit Payment Method -->
<ds-modal
  id="autopayEditModalId"
  class="paymentModal"
  [closeOthers]="true"
  [maxWidth]="editEnabledFor === 'paymentMethod' ? 750 : 340"
  [fullScreen]="true"
  [defaultCloseButton]="true"
  (onCloseModalEvent)="resetAutoPayForm()"
>
  @if (selectedBillAccount !== '') {
    <ds-auto-pay-setup-wrapper
      [newBillingDesign]="true"
      [enabledEdit]="editEnabledFor"
      [selectedBillAccount]="selectedBillAccount"
      (confirmStatusEvent)="corfirmSubmissionStatus($event)"
    ></ds-auto-pay-setup-wrapper>
  }
</ds-modal>

<ds-modal
  id="deleteAutopayModalId"
  class="deleteModal"
  [closeOthers]="true"
  maxWidth="750"
  [fullScreen]="true"
  [defaultCloseButton]="true"
>
  <div class="delete-autopay-modal">
    <div
      class="header font-weight-semibold"
      dsContent
      [contentPath]="'billing.autoPayRefactor.deleteModalHeadingText'"
    ></div>
    <div class="height-font-1 font-size-1 neutral-800">
      @if (displayDeleteAutoPayDiscountMessage$ | async) {
        <div
          dsContent
          [contentPath]="'billing.autoPayRefactor.deleteModalBodyTextAutoPayDiscount'"
        ></div>
      } @else {
        <div dsContent [contentPath]="'billing.autoPayRefactor.deleteModalBodyText'"></div>
      }
    </div>
    <div class="button-container">
      <button
        ds-button
        class="-outline pointer-cursor"
        (click)="closeDeleteAutopayModal()"
        dsContent
        [contentPath]="'billing.manageAutopay.cancelBtn'"
      ></button>
      <button
        ds-button
        class="red pointer-cursor"
        (click)="deleteAutoPayConfirmed()"
        dsContent
        [contentPath]="'billing.manageAutopay.deleteComfBtn'"
      ></button>
    </div>
  </div>
</ds-modal>

<ds-choose-payment-method-modal
  (addCheckingAccountEvent)="addCheckingAccount()"
  (addCreditDebitEvent)="addCreditDebit()"
/>

<ds-payment-method-modal
  [paymentMethodAddEditConfig]="paymentMethodAddEditConfig$ | async"
  (paymentMethodAddEditCompleteEvent)="paymentMethodAddEditComplete($event)"
>
</ds-payment-method-modal>
